import { useEffect, useState } from 'react';

import { ReactComponent as Trash } from 'design_system/src/static/icons/trash.svg';

import {
	Button,
	ListTable,
	Pager,
	Select,
	SideModal,
	Typography,
} from 'design_system/src';
import useFindAllParams from 'hooks/useFindAllParams';
import { findAllLeads } from 'models/lead/controllers/lead.controller';
import { Lead } from 'models/lead/interfaces/lead.interface';
import { LeadSortType } from 'models/lead/enums/lead.enums';
import { formatValuesIfNotMaxLength } from 'models/shared/helpers/utils';
import motoLoading from 'static/icons/loading_moto.gif';
import SearchBar from '../../components/hocs/searchBar/searchBar';
import LeadDetails from './LeadDetails';
import {
	leadSortOptions,
	leadStatusOptions,
	leadStepOptions,
	leadsListColumns,
} from './constants';

function Leads() {
	const {
		findAllParams: { data, currentPage, pages, loading, query, count },
		setData,
		setCurrentPage,
		setPages,
		setLoading,
		setQuery,
		setCount,
	} = useFindAllParams<Lead, never>();
	const [isMounted, setIsMounted] = useState(false);
	const [selectedStep, setSelectedStep] = useState<string[]>([]);
	const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
	const [selectedSort, setSelectedSort] = useState<LeadSortType>(
		LeadSortType.createdAtNewer,
	);
	const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
	const [openDetail, setOpenDetail] = useState(false);

	const requestData = () => {
		setLoading(true);
		findAllLeads({
			step: formatValuesIfNotMaxLength(selectedStep, leadStepOptions.length),
			status: formatValuesIfNotMaxLength(
				selectedStatus,
				leadStatusOptions.length,
			),
			query: query || undefined,
			sort: selectedSort || undefined,
			page: currentPage,
			limit: 30,
		})
			.then((result) => {
				setData(result?.data ?? []);
				setPages(result?.pages ?? 0);
				setCount(result?.count ?? 0);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
				setData([]);
			});
	};

	useEffect(() => {
		setIsMounted(true);
	}, []);

	useEffect(() => {
		if (!isMounted) return;
		if (currentPage === 1) {
			requestData();
		} else {
			setCurrentPage(1);
		}
	}, [isMounted, selectedStep, selectedStatus, selectedSort]);

	useEffect(() => {
		if (!isMounted) return;
		requestData();
	}, [currentPage]);

	const handleSearch = (event: any) => {
		event.preventDefault();
		if (currentPage === 1) {
			requestData();
		} else {
			setCurrentPage(1);
		}
	};

	const hasActiveFilters = !!(selectedStep.length || selectedStatus.length);

	const cleanFilter = () => {
		setSelectedStep([]);
		setSelectedStatus([]);
	};

	const handleRowClick = (item: Lead) => {
		setSelectedLeadId(item._id);
		setOpenDetail(true);
	};

	const renderBody = () => {
		if (loading) {
			return (
				<div className="flex_center_col dim_100_per">
					<Typography
						weight="600"
						scale="heading4"
						textColor="neutral_800"
						className="m_b_xs"
					>
						Cargando leads...
					</Typography>
					<img src={motoLoading} alt="" className="h_xxxl" />
				</div>
			);
		}
		if (data.length) {
			return (
				<ListTable
					data={data}
					onRowClick={handleRowClick}
					canSelect={false}
					rowClassName="br_none"
					className="m_t_md"
					cols={leadsListColumns()}
				/>
			);
		}
		return (
			<div className="flex_center dim_100_per">
				<Typography
					scale="heading4"
					weight="600"
					className="text_neutral_900 m_xl text_center"
				>
					{query
						? 'Tus filtros no generaron resultados'
						: 'No hay leads creados'}
				</Typography>
			</div>
		);
	};

	return (
		<div className="dim_100_per flex_content_col shadow_hard inner_container bg_neutral_400">
			<div className="flex_header">
				<div className="bg_neutral_0 shadow_hard p_t_xs p_b_md p_x_lg">
					<div className="display_flex flex_gap_sm flex_align_center">
						<Typography scale="heading3" weight="600" textColor="neutral_900">
							Leads
						</Typography>
						<SearchBar
							sortOptions={leadSortOptions}
							sortValue={selectedSort}
							setsortValue={(value: string) =>
								setSelectedSort(value as LeadSortType)
							}
							handleSearch={handleSearch}
							query={query}
							setQuery={setQuery}
							className="flex_grow_1"
						/>
					</div>
					<div className="display_flex flex_align_center flex_wrap flex_gap_sm">
						<Select
							fixedPlaceholder
							variant="checkbox"
							placeholder="Paso"
							options={leadStepOptions}
							value={selectedStep}
							onChange={(e: { target: { value: string[] } }) =>
								setSelectedStep(e.target.value)
							}
						/>
						<Select
							fixedPlaceholder
							variant="checkbox"
							placeholder="Estado"
							options={leadStatusOptions}
							value={selectedStatus}
							onChange={(e: { target: { value: string[] } }) =>
								setSelectedStatus(e.target.value)
							}
						/>
						{hasActiveFilters && (
							<Button
								variant="ghost"
								scale="small"
								icon={<Trash />}
								onClick={() => cleanFilter()}
								color="primary"
							>
								Limpiar Filtro
							</Button>
						)}
					</div>
				</div>
			</div>

			<div className="flex_body overflow_x_hidden p_x_lg">{renderBody()}</div>

			<div className="display_flex flex_align_center flex_justify_between flex_footer bg_neutral_0 p_x_xxxl p_y_md">
				<div />
				<Pager
					totalPages={pages ?? 0}
					page={currentPage}
					setPage={setCurrentPage}
					count={count}
					className="w_fit"
				/>
			</div>

			<SideModal
				open={openDetail}
				fullOpen={false}
				setFullOpen={() => {}}
				setOpen={setOpenDetail}
				size="medium"
			>
				{openDetail && selectedLeadId && (
					<LeadDetails leadId={selectedLeadId} />
				)}
			</SideModal>
		</div>
	);
}

export default Leads;
